import React, { useState, useRef, useCallback, useEffect } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import { SERVICE_TYPES } from './mockData'
import { IconUpload, IconPlus, IconClose, IconFile, IconChevron, IconSquarePlus, ICON_MAP } from './icons'

const SOURCE_OPTIONS = [
  { value: 'phone_call', label: 'Phone call' },
  { value: 'voicemail', label: 'Voicemail (VoIP)' },
  { value: 'sms', label: 'SMS (VoIP)' },
  { value: 'email', label: 'Email' },
  { value: 'field_observation', label: 'Field observation' },
  { value: 'aspire_work_order', label: 'Aspire work order' },
  { value: 'customer_portal', label: 'Customer portal' },
  { value: 'walk_in', label: 'Walk-in' },
]

function formatAge(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 60) return `${mins}m`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  return `${days}d`
}

function formatSize(bytes) {
  if (!bytes) return ''
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1048576) return (bytes / 1024).toFixed(0) + ' KB'
  return (bytes / 1048576).toFixed(1) + ' MB'
}

export default function UnclaimedWorkPanel({ url, expanded, onToggle, onSubmit, onSaveDraft, items = [], stats = {} }) {
  const [dragOver, setDragOver] = useState(false)
  const [files, setFiles] = useState([])
  const [priority, setPriority] = useState('normal')
  const [selectedTags, setSelectedTags] = useState([])
  const [body, setBody] = useState('')
  const [contactId, setContactId] = useState('')
  const [contactName, setContactName] = useState('')
  const [contactSearch, setContactSearch] = useState('')
  const [contactResults, setContactResults] = useState([])
  const [showContactDropdown, setShowContactDropdown] = useState(false)
  const [dueDate, setDueDate] = useState('')
  const [source, setSource] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [targetBid, setTargetBid] = useState(null)
  const [activeBids, setActiveBids] = useState([])
  const [contextType, setContextType] = useState('note')
  const [bidSearch, setBidSearch] = useState('')
  const [showBidDropdown, setShowBidDropdown] = useState(false)
  const fileInputRef = useRef(null)
  const contactSearchTimer = useRef(null)

  // Fetch active bids for quick-capture dropdown
  useEffect(() => {
    if (!url) return
    axios.get(`${url}/api/hmi/dashboard/active-bids`)
      .then(res => setActiveBids(res.data?.data || []))
      .catch(() => {})
  }, [url])

  // Search contacts as user types
  useEffect(() => {
    if (!url || contactSearch.length < 2) {
      setContactResults([])
      return
    }
    clearTimeout(contactSearchTimer.current)
    contactSearchTimer.current = setTimeout(() => {
      axios.get(`${url}/api/hmi/unclaimed-work/contacts`, { params: { q: contactSearch } })
        .then(res => {
          if (res.data?.success) {
            setContactResults(res.data.data)
            setShowContactDropdown(true)
          }
        })
        .catch(() => {})
    }, 300)
  }, [contactSearch, url])

  const uploadFile = useCallback(async (file) => {
    try {
      const presignRes = await axios.post(`${url}/api/hmi/unclaimed-work/presigned-url`, {
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
      })
      if (!presignRes.data?.success) throw new Error('Failed to get upload URL')
      const { uploadUrl, publicUrl } = presignRes.data.data

      await axios.put(uploadUrl, file, {
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      })
      return { filename: file.name, url: publicUrl, size: file.size, contentType: file.type }
    } catch (err) {
      console.error('[UNCLAIMED] File upload failed:', err)
      return null
    }
  }, [url])

  const handleDragOver = useCallback((e) => { e.preventDefault(); setDragOver(true) }, [])
  const handleDragLeave = useCallback(() => setDragOver(false), [])

  const processFiles = useCallback(async (fileList) => {
    const newFiles = Array.from(fileList).map(f => ({ name: f.name, size: f.size, uploading: true }))
    setFiles(prev => [...prev, ...newFiles])
    for (const rawFile of Array.from(fileList)) {
      const result = await uploadFile(rawFile)
      setFiles(prev => prev.map(f => {
        if (f.name === rawFile.name && f.uploading) {
          return result ? { ...result, uploading: false } : { ...f, uploading: false, error: true }
        }
        return f
      }))
    }
  }, [uploadFile])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    setDragOver(false)
    processFiles(e.dataTransfer.files)
  }, [processFiles])

  const handleFileSelect = useCallback((e) => {
    processFiles(e.target.files)
    e.target.value = ''
  }, [processFiles])

  const removeFile = useCallback((idx) => { setFiles(prev => prev.filter((_, i) => i !== idx)) }, [])
  const toggleTag = useCallback((label) => {
    setSelectedTags(prev => prev.includes(label) ? prev.filter(t => t !== label) : [...prev, label])
  }, [])

  const selectContact = useCallback((contact) => {
    setContactId(contact._id)
    setContactName(`${contact.firstName} ${contact.lastName}`.trim())
    setContactSearch(`${contact.firstName} ${contact.lastName}`.trim())
    setShowContactDropdown(false)
  }, [])

  const resetForm = useCallback(() => {
    setFiles([]); setPriority('normal'); setSelectedTags([]); setBody('')
    setContactId(''); setContactName(''); setContactSearch(''); setDueDate(''); setSource('')
    setTargetBid(null); setContextType('note'); setBidSearch('')
  }, [])

  const buildFormData = useCallback(() => ({
    title: body.substring(0, 120),
    body,
    priority,
    due_date: dueDate || undefined,
    contact: contactId || undefined,
    contact_name: contactName,
    service_types: selectedTags,
    source: source || undefined,
    attachments: files.filter(f => !f.uploading && !f.error).map(f => ({
      filename: f.filename || f.name, url: f.url, size: f.size, contentType: f.contentType,
    })),
  }), [body, priority, dueDate, contactId, contactName, selectedTags, source, files])

  const handleSubmitClick = useCallback(async () => {
    if (targetBid) {
      // Build context record from capture form
      const titleText = (body || '').trim().substring(0, 120) || 'Quick capture'
      const contextBody = {
        title: titleText,
        body: body || '',
        priority,
        parent_type: 'estimate',
      }

      // Map source to interaction_type for conversations
      if (contextType === 'conversation') {
        const sourceToInteraction = { phone_call: 'call', voicemail: 'call', sms: 'call', email: 'email', field_observation: 'site_visit', walk_in: 'meeting' }
        contextBody.interaction_type = sourceToInteraction[source] || 'other'
        contextBody.contact_name = contactName || ''
        contextBody.occurred_at = new Date().toISOString()
      }
      if (contextType === 'blocker') {
        contextBody.waiting_on_name = contactName || ''
        if (dueDate) contextBody.expected_response_at = dueDate
      }
      if (contextType === 'task') {
        contextBody.description = body || ''
        if (dueDate) contextBody.dueDate = dueDate
      }

      const typeToEndpoint = {
        note: 'notes',
        blocker: 'blockers',
        task: 'tasks',
        conversation: 'conversations'
      }

      try {
        setSubmitting(true)
        await axios.post(`${url}/api/bid-estimation/projects/${targetBid._id}/${typeToEndpoint[contextType]}`, contextBody)
        resetForm()
        setSubmitting(false)
        alert(`${contextType.charAt(0).toUpperCase() + contextType.slice(1)} added to ${targetBid.name}`)
      } catch (err) {
        setSubmitting(false)
        alert('Failed to add to bid: ' + (err.response?.data?.error || err.message))
      }
      return
    }

    setSubmitting(true)
    const success = await onSubmit(buildFormData())
    if (success) resetForm()
    setSubmitting(false)
  }, [onSubmit, buildFormData, resetForm, targetBid, body, priority, contextType, source, contactName, dueDate, url])

  const handleDraftClick = useCallback(async () => {
    const success = await onSaveDraft(buildFormData())
    if (success) resetForm()
  }, [onSaveDraft, buildFormData, resetForm])

  return (
    <div className="panel">
      <div className="panel-head" style={{ cursor: 'pointer' }} onClick={onToggle}>
        <h4 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <IconSquarePlus size={16} style={{ color: 'var(--warn)' }} />
          Unclaimed Work
          <span className="pill pill-warn" style={{ fontSize: '9.5px' }}>
            <span className="dot" />{stats.submitted || 0} pending
          </span>
        </h4>
        <div className="right" style={{ gap: 10 }}>
          <Link to="/unclaimed-work" style={{ fontSize: 12, color: 'var(--brand-deep)', textDecoration: 'none', fontWeight: 550 }} onClick={e => e.stopPropagation()}>View all &rarr;</Link>
          <span className={`chevron-icon${expanded ? '' : ' collapsed'}`} style={{ color: 'var(--ink-3)' }}>
            <IconChevron size={12} />
          </span>
        </div>
      </div>

      <div className={`unclaimed-body${expanded ? '' : ' collapsed'}`}>
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Dropzone */}
          <div
            className={`dropzone${dragOver ? ' drag-over' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="dropzone-inner">
              <div className="dropzone-icon"><IconUpload size={24} /></div>
              <div className="dropzone-text">
                <span style={{ fontWeight: 600, color: 'var(--ink-1)' }}>Drop files here</span>
                <span style={{ color: 'var(--ink-3)', fontSize: 12 }}>
                  or <button className="dropzone-browse" onClick={() => fileInputRef.current?.click()}>browse</button>
                </span>
                <span style={{ color: 'var(--ink-4)', fontSize: 11, fontFamily: 'var(--f-mono)' }}>
                  Photos, PDFs, emails &middot; max 25 MB
                </span>
              </div>
              <input type="file" ref={fileInputRef} multiple style={{ display: 'none' }} onChange={handleFileSelect} />
            </div>
            {files.length > 0 && (
              <div className="file-list">
                {files.map((f, i) => (
                  <div className={`file-chip${f.uploading ? ' uploading' : ''}${f.error ? ' error' : ''}`} key={i}>
                    <span className="file-icon"><IconFile size={14} /></span>
                    {f.filename || f.name}
                    <span className="file-size">
                      {f.uploading ? 'uploading...' : f.error ? 'failed' : formatSize(f.size)}
                    </span>
                    <button className="file-remove" onClick={(e) => { e.stopPropagation(); removeFile(i) }}>
                      <IconClose size={10} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="field-group">
            <label className="eyebrow">DESCRIPTION / NOTES</label>
            <textarea
              className="field-input"
              rows={3}
              placeholder='What&apos;s the work? e.g. "Customer called about dead turf on the east median — needs site visit"'
              value={body}
              onChange={e => setBody(e.target.value)}
            />
          </div>

          {/* Target Bid (optional) */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div className="field-group" style={{ flex: 1, position: 'relative' }}>
              <label className="eyebrow">TARGET BID <span style={{ color: 'var(--ink-4)', fontWeight: 400, fontSize: 10 }}>(optional)</span></label>
              <input
                className="field-input"
                placeholder="Search bids..."
                value={targetBid ? `${targetBid.name}${targetBid.client ? ' \u00b7 ' + targetBid.client : ''}` : bidSearch}
                onChange={e => {
                  if (targetBid) { setTargetBid(null) }
                  setBidSearch(e.target.value)
                  setShowBidDropdown(true)
                }}
                onFocus={() => setShowBidDropdown(true)}
                onBlur={() => setTimeout(() => setShowBidDropdown(false), 200)}
              />
              {targetBid && (
                <button
                  style={{ position: 'absolute', right: 8, top: 28, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-4)' }}
                  onClick={() => { setTargetBid(null); setBidSearch(''); setContextType('note') }}
                  title="Clear"
                >&times;</button>
              )}
              {showBidDropdown && !targetBid && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                  background: '#fff', border: '1px solid var(--line)', borderRadius: 6,
                  maxHeight: 200, overflow: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}>
                  {activeBids
                    .filter(b => !bidSearch || b.name.toLowerCase().includes(bidSearch.toLowerCase()) || (b.client || '').toLowerCase().includes(bidSearch.toLowerCase()))
                    .slice(0, 10)
                    .map(b => (
                      <div
                        key={b._id}
                        style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13 }}
                        className="contact-option"
                        onMouseDown={() => { setTargetBid(b); setBidSearch(''); setShowBidDropdown(false) }}
                      >
                        <div style={{ fontWeight: 500 }}>{b.name}</div>
                        {b.client && <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{b.client}</div>}
                      </div>
                    ))
                  }
                  {activeBids.filter(b => !bidSearch || b.name.toLowerCase().includes(bidSearch.toLowerCase())).length === 0 && (
                    <div style={{ padding: '8px 12px', color: 'var(--ink-3)', fontSize: 12 }}>No matching bids</div>
                  )}
                </div>
              )}
            </div>

            {/* Context type selector — only shown when a bid is selected */}
            {targetBid && (
              <div className="field-group">
                <label className="eyebrow">TYPE</label>
                <select
                  className="field-input field-select"
                  value={contextType}
                  onChange={e => setContextType(e.target.value)}
                  style={{ minWidth: 130 }}
                >
                  <option value="note">Note</option>
                  <option value="blocker">Blocker</option>
                  <option value="task">Task</option>
                  <option value="conversation">Conversation</option>
                </select>
              </div>
            )}
          </div>

          {/* Row: Contact + Date + Priority */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <div className="field-group" style={{ position: 'relative' }}>
              <label className="eyebrow">CONTACT</label>
              <input
                className="field-input"
                placeholder="Search contacts..."
                value={contactSearch}
                onChange={e => { setContactSearch(e.target.value); setContactId('') }}
                onFocus={() => contactResults.length > 0 && setShowContactDropdown(true)}
                onBlur={() => setTimeout(() => setShowContactDropdown(false), 200)}
              />
              {showContactDropdown && contactResults.length > 0 && (
                <div className="contact-dropdown">
                  {contactResults.map(c => (
                    <div key={c._id} className="contact-option" onMouseDown={() => selectContact(c)}>
                      <span className="contact-name">{c.firstName} {c.lastName}</span>
                      {c.client?.name && <span className="contact-company">{c.client.name}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="field-group">
              <label className="eyebrow">NEEDED BY</label>
              <input type="date" className="field-input" value={dueDate} onChange={e => setDueDate(e.target.value)} />
            </div>
            <div className="field-group">
              <label className="eyebrow">PRIORITY</label>
              <div className="priority-picker">
                {['low', 'normal', 'urgent'].map(level => (
                  <button
                    key={level}
                    className={`priority-opt${priority === level ? ' active' : ''}${priority === level ? ' ' + level : ''}`}
                    onClick={() => setPriority(level)}
                  >
                    <span className="pri-dot" style={{ background: level === 'low' ? 'var(--ok)' : level === 'urgent' ? 'var(--risk)' : 'var(--warn)' }} />
                    {level.charAt(0).toUpperCase() + level.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Row: Tags + Source */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="field-group">
              <label className="eyebrow">SERVICE TYPE</label>
              <div className="tag-row">
                {SERVICE_TYPES.map((t) => (
                  <button
                    key={t.label}
                    className={`tag-btn${selectedTags.includes(t.label) ? ' selected' : ''}`}
                    onClick={() => toggleTag(t.label)}
                  >
                    <span className="tag-dot" style={{ background: t.dotColor }} />
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="field-group">
              <label className="eyebrow">SOURCE</label>
              <select className="field-input field-select" value={source} onChange={e => setSource(e.target.value)}>
                <option value="">Where did this come from?</option>
                {SOURCE_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 2 }}>
            <button
              className="dbtn dbtn-primary"
              style={{ height: 32 }}
              onClick={handleSubmitClick}
              disabled={submitting || !body.trim()}
            >
              <IconPlus size={14} />
              {submitting ? 'Submitting...' : targetBid ? `Add to ${targetBid.name}` : 'Submit to queue'}
            </button>
            <button className="dbtn dbtn-outline" style={{ height: 32 }} onClick={handleDraftClick}>Save as draft</button>
            <span style={{ marginLeft: 'auto', fontSize: '11.5px', color: 'var(--ink-3)' }}>
              Submits to unclaimed queue for assignment
            </span>
          </div>
        </div>

        {/* Recent unclaimed items from API */}
        <div style={{ borderTop: '1px solid var(--line-1, var(--line))' }}>
          <div style={{ padding: '8px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span className="eyebrow" style={{ fontSize: 10 }}>RECENT UNCLAIMED</span>
            <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>Oldest first</span>
          </div>
          {items.length === 0 && (
            <div style={{ padding: '16px 14px', textAlign: 'center', color: 'var(--ink-3)', fontSize: 12 }}>
              No unclaimed work items
            </div>
          )}
          {items.slice(0, 5).map((item) => {
            const itemType = item.priority === 'urgent' ? 'warn' : 'info'
            const Ic = ICON_MAP[item.priority === 'urgent' ? 'warning' : 'grid']
            const age = item.createdAt ? formatAge(item.createdAt) : ''
            const srcLabel = SOURCE_OPTIONS.find(s => s.value === item.source)?.label
            return (
              <div className={`list-item ${itemType}`} key={item._id}>
                <div className="ic">{Ic && <Ic size={14} />}</div>
                <div className="item-text">
                  <div className="item-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {item.title || item.body?.substring(0, 80) || 'Untitled'}
                    {item.priority === 'urgent' && (
                      <span className="pill pill-warn" style={{ fontSize: 9 }}><span className="dot" />Urgent</span>
                    )}
                  </div>
                  <div className="item-desc">
                    {[srcLabel, item.contact_name, item.service_types?.join(', ')].filter(Boolean).join(' \u00b7 ')}
                  </div>
                </div>
                <span className="ts">{age}</span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
